<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Payment',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    invoices: {
      type: Array,
      required: false,
      default: null,
    },
    accountId: {
      type: String,
      required: false,
    },
    transactionKind: {
      type: String,
      required: false,
      default: null,
    },
    transactionType: {
      type: String,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const recalculateAmountFromLines = (...args) => {
    const _item = item.value;

    if (_item.isReadonly) {
      return;
    }

    const [invoiceData] = args;

    if (invoiceData) {
      const idx = _.findIndex(_item.invoices.data, {
        invoice_id: invoiceData.invoice_id,
      });
      if (idx > -1) {
        _.extend(_item.invoices.data[idx], invoiceData);
      }
    }

    if (!autocalcAmount.value) {
      return;
    }

    const summa = $fn.value.numeral(0);

    if (!_item.invoices || !_item.invoices.data) {
      return;
    }

    for (let d of _item.invoices.data) {
      if (d.amount == null) {
        continue;
      }
      summa.add(d.amount);
    }

    _item.amount = summa.value();

    if (isPettyCashPayment.value) {
      _item.amount = Math.abs(_item.amount);
    }
  };
  const reloadTemplate = (...args) => {
    loadData('item');
  };
  const addPendingAttachment = (...args) => {
    const [payload] = args;
    if (!payload || !payload.file) {
      return;
    }
    pendingAttachments.value = [
      ...pendingAttachments.value,
      {
        _uid: $fn.value.randomElementId(),
        file: payload.file,
        description: payload.description || null,
      },
    ];
  };
  const removePendingAttachment = (...args) => {
    const [uid] = args;
    pendingAttachments.value = pendingAttachments.value.filter(
      (p) => p._uid !== uid
    );
  };
  const uploadPendingAttachments = (...args) => {
    const pending = pendingAttachments.value;
    const ownerId = item.value && item.value.id;

    if (!ownerId || !pending || !pending.length) {
      return;
    }

    return Promise.all(
      pending.map((p) =>
        $fn.value.fetch('create_attachment', {
          contentType: 'multipart/form-data',
          content: {
            object_id: ownerId,
            description: p.description,
            file: p.file,
          },
        })
      )
    ).then(() => {
      pendingAttachments.value = [];
      attachmentsReload.value = Date.now();
    });
  };

  const formId = ref(null);
  const pendingReload = ref(false);
  const pendingAttachments = ref([]);
  const attachmentsReload = ref(0);
  const autocalcAmount = ref(true);
  const item = ref(null);
  const allConfig = ref({
    transactionTypes: {
      bank: {
        credit: $fn.value.pgettext('TransactionType', 'credit'),
        debit: $fn.value.pgettext('TransactionType', 'debit'),
      },
      cash_book: {
        deposit: $fn.value.pgettext('TransactionType', 'deposit'),
        payment: $fn.value.pgettext('TransactionType', 'payment'),
      },
      technical: {
        invoice_reconciliation: $fn.value.pgettext(
          'TransactionType',
          'invoice reconciliation'
        ),
        write_off: $fn.value.pgettext('TransactionType', 'write off'),
      },
    },
    bank: {
      credit: {
        fields: {},
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Bank credit'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      debit: {
        fields: {},
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Bank debit'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
    cash_book: {
      deposit: {
        fields: {
          transaction_number: false,
          transaction_type: 'ro',
          exchange_rate: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Cash-In'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      payment: {
        fields: {
          transaction_number: false,
          transaction_type: 'ro',
          exchange_rate: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Cash-Out'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
    technical: {
      invoice_reconciliation: {
        fields: {
          amount: false,
          exchange_rate: false,
          transaction_number: false,
          payment_method: false,
          employee: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext(
          'Payment|Header',
          'Invoice reconciliation'
        ),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      write_off: {
        fields: {
          amount: false,
          exchange_rate: false,
          transaction_number: false,
          payment_method: false,
          employee: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Write off'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    pendingReload: pendingReload.value,
    pendingAttachments: pendingAttachments.value,
    attachmentsReload: attachmentsReload.value,
    autocalcAmount: autocalcAmount.value,
    item: item.value,
    allConfig: allConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return props.oid == null
      ? $fn.value.fetch('get_payment_template', {
          asResource: true,
          parameters: {
            kind: props.transactionKind,
            type: props.transactionType,
            invoices: props.invoices ? props.invoices.join(',') : null,
            account_id:
              ($data.value.item &&
                $data.value.item.account &&
                $data.value.item.account.id) ||
              props.accountId,
          },
        })
      : $fn.value.fetch('read_payment', {
          asResource: true,
          parameters: { id: props.oid },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'pendingReload' },
      { name: 'pendingAttachments' },
      { name: 'attachmentsReload' },
      {
        name: 'autocalcAmount',
        cache: { type: 'local', key: 'PaymentInvoices.autocalcAmount' },
      },
      { name: 'item' },
      { name: 'allConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      pendingReload,
      pendingAttachments,
      attachmentsReload,
      autocalcAmount,
      item,
      allConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const isNew = computed(() => {
    if (loaded.value) {
      return item.value && item.value.id == null;
    }
    return null;
  });
  const isPettyCashPayment = computed(() => {
    if (loaded.value) {
      return (
        item.value.account && item.value.account.account_type === 'cash_book'
      );
    }
    return null;
  });
  const config = computed(() => {
    if (loaded.value) {
      return allConfig.value[item.value.transaction_kind][
        item.value.transaction_type
      ];
    }
    return null;
  });
  const transactionTypes = computed(() => {
    if (loaded.value) {
      return allConfig.value.transactionTypes[item.value.transaction_kind];
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: config.value ? config.value.resourceTitle : '' };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );
  watch(
    () => props.accountId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('accountId');
    }
  );
  watch(
    () => props.transactionKind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionKind');
    }
  );
  watch(
    () => props.transactionType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionType');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_dlgtgztit_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_8564662037462133_closeModal_xzjmyyany_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_6466705138206980_closeModal_yq31l7u7v_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_dskqhkk38_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_setData_0h976wq5a_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event && $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_0h976wq5a_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_script_qhq2s4c16_value_callable = async (
    $event
  ) => {
    return uploadPendingAttachments();
  };
  const act_1040167445267876_script_qhq2s4c16_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_qhq2s4c16_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_qhq2s4c16_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_setData_bwvvnh5ru_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_bwvvnh5ru_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_reloadGrid_mue9kv5bk_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_reloadGrid_mue9kv5bk_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_crud_zdkvs5btg_getActionArgs = async ($event) => {
    return {
      objectType: 'payment',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_5534025912102772_script_5qam34uf8_value_callable = async (
    $event
  ) => {
    return reloadTemplate($event.data);
  };
  const act_5534025912102772_script_5qam34uf8_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_5qam34uf8_value_callable($event);
      },
    };
  };
  const act_5534025912102772_setData_rpjoxs73y_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'autocalcAmount',
      value: $event.originalEvent,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6802360265496969_script_1t4clj6oa_value_callable = async (
    $event
  ) => {
    return addPendingAttachment($event.data);
  };
  const act_6802360265496969_script_1t4clj6oa_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6802360265496969_script_1t4clj6oa_value_callable($event);
      },
    };
  };
  const act_6802360265496969_script_9r18pavkm_value_callable = async (
    $event
  ) => {
    return removePendingAttachment($event.data);
  };
  const act_6802360265496969_script_9r18pavkm_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6802360265496969_script_9r18pavkm_value_callable($event);
      },
    };
  };
  const act_3083451129821185_script_hp9pdpgax_value_callable = async (
    $event
  ) => {
    return recalculateAmountFromLines($event.data);
  };
  const act_3083451129821185_script_hp9pdpgax_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3083451129821185_script_hp9pdpgax_value_callable($event);
      },
    };
  };
  const act_2434514588044777_setData_hvr0z4q9v_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event && $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_2434514588044777_success_a5a7n0y09_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_setData_lmznedxc4_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_2434514588044777_success_9u0m5zxba_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_reloadGrid_6w5mq9t2n_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const inst_evh_2434514588044777_success_dbnsu3uf5_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_rest_uga3boyoh_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: {
        self: item.value.links.parent,
        object: 'list',
        data: [],
      },
      resource: item.value,
      data: {
        etag: item.value.etag,
        complete: true,
      },
    };
  };
  const act_2434514588044777_rest_uga3boyoh_getConfirm = async ($event) => {
    return {
      header: config.value ? config.value.resourceTitle : '',
      message: $fn.value.gettext(
        'Completing prevents further modifications. Please confirm this operation.'
      ),
      btnOk: $fn.value.gettext('Complete'),
      severity: 'warning',
    };
  };
  const act_7253299514750264_setData_ruvh2un8g_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: item.value.clone({
        id: null,
        rev: null,
        transaction_number: isPettyCashPayment.value
          ? null
          : item.value.transaction_number,
        amount: 0,
        links: null,
        etag: null,
        invoices: { data: [] },
        preview_url: null,
        locked: false,
        notes: null,
        internal_notes: null,
      }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7253299514750264_setData_w0o3yc7bq_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingAttachments',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_dlgtgztit =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_dlgtgztit_getActionArgs,
    });
  const inst_evh_7315092382398562_close_d111ikum3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_dlgtgztit,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_d111ikum3],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_sv7eo89pk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_iglp7utka =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_sv7eo89pk,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_iglp7utka],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_xzjmyyany =
    new actionTypes.CloseModalAction({
      actionArgs: act_8564662037462133_closeModal_xzjmyyany_getActionArgs,
    });
  const inst_evh_8564662037462133_close_csfxghujy =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_xzjmyyany,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_csfxghujy],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_ijs99ruix =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_pb8lei3wq =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_ijs99ruix,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_pb8lei3wq],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_yq31l7u7v =
    new actionTypes.CloseModalAction({
      actionArgs: act_6466705138206980_closeModal_yq31l7u7v_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_ya3i9nn2h =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_yq31l7u7v,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_6466705138206980_reloadGrid_dskqhkk38 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_dskqhkk38_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_orftdte2f =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_dskqhkk38,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_ya3i9nn2h,
      inst_evh_6466705138206980_deleted_orftdte2f,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_setData_0h976wq5a = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_0h976wq5a_getActionArgs,
    when: act_1040167445267876_setData_0h976wq5a_getWhen,
  });
  const inst_evh_1040167445267876_success_4lljd4npw =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_0h976wq5a,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_script_qhq2s4c16 = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_qhq2s4c16_getActionArgs,
    when: act_1040167445267876_script_qhq2s4c16_getWhen,
  });
  const inst_evh_1040167445267876_success_ryhes8xbc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_qhq2s4c16,
      event: 'success',
      displayName: 'script',
    });
  const act_1040167445267876_setData_bwvvnh5ru = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_bwvvnh5ru_getActionArgs,
    when: act_1040167445267876_setData_bwvvnh5ru_getWhen,
  });
  const inst_evh_1040167445267876_success_cf0ifv2hg =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_bwvvnh5ru,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_reloadGrid_mue9kv5bk =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_mue9kv5bk_getActionArgs,
      when: act_1040167445267876_reloadGrid_mue9kv5bk_getWhen,
    });
  const inst_evh_1040167445267876_success_q87jiqg2i =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_mue9kv5bk,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_crud_zdkvs5btg = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_zdkvs5btg_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_4lljd4npw,
      inst_evh_1040167445267876_success_ryhes8xbc,
      inst_evh_1040167445267876_success_cf0ifv2hg,
      inst_evh_1040167445267876_success_q87jiqg2i,
    ],
  });
  const inst_evh_1040167445267876_submit_d42wd5lhd =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_zdkvs5btg,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_d42wd5lhd],
    event: 'submit',
  });
  const act_5534025912102772_script_5qam34uf8 = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_5qam34uf8_getActionArgs,
  });
  const inst_evh_5534025912102772_accountChanged_8384ionys =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_5qam34uf8,
      event: 'accountChanged',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_accountChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_accountChanged_8384ionys],
      event: 'accountChanged',
    });
  const act_5534025912102772_setData_rpjoxs73y = new actionTypes.SetDataAction({
    actionArgs: act_5534025912102772_setData_rpjoxs73y_getActionArgs,
  });
  const inst_evh_5534025912102772_autocalcAmountChanged_3ck0mir74 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_setData_rpjoxs73y,
      event: 'autocalcAmountChanged',
      displayName: 'setData',
    });
  const inst_evh_5534025912102772_autocalcAmountChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_autocalcAmountChanged_3ck0mir74],
      event: 'autocalcAmountChanged',
    });
  const act_6802360265496969_script_1t4clj6oa = new actionTypes.ScriptAction({
    actionArgs: act_6802360265496969_script_1t4clj6oa_getActionArgs,
  });
  const inst_evh_6802360265496969_stage_rc8iartrz =
    new actionTypes.EventHandlerImpl({
      action: act_6802360265496969_script_1t4clj6oa,
      event: 'stage',
      displayName: 'script',
    });
  const inst_evh_6802360265496969_stage = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6802360265496969_stage_rc8iartrz],
    event: 'stage',
  });
  const act_6802360265496969_script_9r18pavkm = new actionTypes.ScriptAction({
    actionArgs: act_6802360265496969_script_9r18pavkm_getActionArgs,
  });
  const inst_evh_6802360265496969_unstage_n3orl2sfe =
    new actionTypes.EventHandlerImpl({
      action: act_6802360265496969_script_9r18pavkm,
      event: 'unstage',
      displayName: 'script',
    });
  const inst_evh_6802360265496969_unstage = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6802360265496969_unstage_n3orl2sfe],
    event: 'unstage',
  });
  const act_3083451129821185_script_hp9pdpgax = new actionTypes.ScriptAction({
    actionArgs: act_3083451129821185_script_hp9pdpgax_getActionArgs,
  });
  const inst_evh_3083451129821185_recalculate_fa5s3ggav =
    new actionTypes.EventHandlerImpl({
      action: act_3083451129821185_script_hp9pdpgax,
      event: 'recalculate',
      displayName: 'script',
    });
  const inst_evh_3083451129821185_recalculate =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3083451129821185_recalculate_fa5s3ggav],
      event: 'recalculate',
    });
  const act_2434514588044777_setData_hvr0z4q9v = new actionTypes.SetDataAction({
    actionArgs: act_2434514588044777_setData_hvr0z4q9v_getActionArgs,
  });
  const inst_evh_2434514588044777_success_a5a7n0y09 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_setData_hvr0z4q9v,
      event: 'success',
      when: inst_evh_2434514588044777_success_a5a7n0y09_getWhen,
      displayName: 'setData',
    });
  const act_2434514588044777_setData_lmznedxc4 = new actionTypes.SetDataAction({
    actionArgs: act_2434514588044777_setData_lmznedxc4_getActionArgs,
  });
  const inst_evh_2434514588044777_success_9u0m5zxba =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_setData_lmznedxc4,
      event: 'success',
      when: inst_evh_2434514588044777_success_9u0m5zxba_getWhen,
      displayName: 'setData',
    });
  const act_2434514588044777_reloadGrid_6w5mq9t2n =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2434514588044777_reloadGrid_6w5mq9t2n_getActionArgs,
    });
  const inst_evh_2434514588044777_success_dbnsu3uf5 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_reloadGrid_6w5mq9t2n,
      event: 'success',
      when: inst_evh_2434514588044777_success_dbnsu3uf5_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2434514588044777_rest_uga3boyoh = new actionTypes.RestAction({
    actionArgs: act_2434514588044777_rest_uga3boyoh_getActionArgs,
    confirm: act_2434514588044777_rest_uga3boyoh_getConfirm,
    displayName: 'complete',
    events: [
      inst_evh_2434514588044777_success_a5a7n0y09,
      inst_evh_2434514588044777_success_9u0m5zxba,
      inst_evh_2434514588044777_success_dbnsu3uf5,
    ],
  });
  const inst_evh_2434514588044777_clickPreventStop_wqrs8lzag =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_rest_uga3boyoh,
      event: 'click.prevent.stop',
      displayName: 'complete',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_wqrs8lzag],
      event: 'click.prevent.stop',
    });
  const act_7253299514750264_setData_ruvh2un8g = new actionTypes.SetDataAction({
    actionArgs: act_7253299514750264_setData_ruvh2un8g_getActionArgs,
  });
  const inst_evh_7253299514750264_clickPreventStop_a5evrktoh =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_setData_ruvh2un8g,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_7253299514750264_setData_w0o3yc7bq = new actionTypes.SetDataAction({
    actionArgs: act_7253299514750264_setData_w0o3yc7bq_getActionArgs,
  });
  const inst_evh_7253299514750264_clickPreventStop_8337gg54y =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_setData_w0o3yc7bq,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_7253299514750264_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7253299514750264_clickPreventStop_a5evrktoh,
        inst_evh_7253299514750264_clickPreventStop_8337gg54y,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_d111ikum3,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_iglp7utka,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_csfxghujy,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_pb8lei3wq,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_ya3i9nn2h,
    inst_evh_6466705138206980_deleted_orftdte2f,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_4lljd4npw,
    inst_evh_1040167445267876_success_ryhes8xbc,
    inst_evh_1040167445267876_success_cf0ifv2hg,
    inst_evh_1040167445267876_success_q87jiqg2i,
    inst_evh_1040167445267876_submit_d42wd5lhd,
    save: inst_evh_1040167445267876_submit_d42wd5lhd,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_accountChanged_8384ionys,
    inst_evh_5534025912102772_accountChanged,
    inst_evh_5534025912102772_autocalcAmountChanged_3ck0mir74,
    inst_evh_5534025912102772_autocalcAmountChanged,
    inst_evh_6802360265496969_stage_rc8iartrz,
    inst_evh_6802360265496969_stage,
    inst_evh_6802360265496969_unstage_n3orl2sfe,
    inst_evh_6802360265496969_unstage,
    inst_evh_3083451129821185_recalculate_fa5s3ggav,
    inst_evh_3083451129821185_recalculate,
    inst_evh_2434514588044777_success_a5a7n0y09,
    inst_evh_2434514588044777_success_9u0m5zxba,
    inst_evh_2434514588044777_success_dbnsu3uf5,
    inst_evh_2434514588044777_clickPreventStop_wqrs8lzag,
    complete: inst_evh_2434514588044777_clickPreventStop_wqrs8lzag,
    inst_evh_2434514588044777_clickPreventStop,
    inst_evh_7253299514750264_clickPreventStop_a5evrktoh,
    inst_evh_7253299514750264_clickPreventStop_8337gg54y,
    inst_evh_7253299514750264_clickPreventStop,
  };

  const evh_7315092382398562_close_d111ikum3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_d111ikum3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_iglp7utka = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_iglp7utka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_csfxghujy = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_csfxghujy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_pb8lei3wq = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_pb8lei3wq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ya3i9nn2h = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ya3i9nn2h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_orftdte2f = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_orftdte2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_4lljd4npw = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_4lljd4npw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ryhes8xbc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ryhes8xbc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_cf0ifv2hg = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_cf0ifv2hg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_q87jiqg2i = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_q87jiqg2i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_d42wd5lhd = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_d42wd5lhd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_accountChanged_8384ionys = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_accountChanged_8384ionys.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_accountChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_accountChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_autocalcAmountChanged_3ck0mir74 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_autocalcAmountChanged_3ck0mir74.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_autocalcAmountChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_autocalcAmountChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_stage_rc8iartrz = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_stage_rc8iartrz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_stage = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_stage.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_unstage_n3orl2sfe = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_unstage_n3orl2sfe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_unstage = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_unstage.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_recalculate_fa5s3ggav = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_recalculate_fa5s3ggav.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_recalculate = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_recalculate.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_a5a7n0y09 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_a5a7n0y09.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_9u0m5zxba = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_9u0m5zxba.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_dbnsu3uf5 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_dbnsu3uf5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_wqrs8lzag = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_wqrs8lzag.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_a5evrktoh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_a5evrktoh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_8337gg54y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_8337gg54y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Payment"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcPaymentHeader
          :payment="item"
          :config="config"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcPaymentHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="payment"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetPaymentGeneral
          :config="config"
          :payment="item"
          :transactionTypes="transactionTypes"
          :formId="formId"
          :reload="lastLoaded"
          :autocalcAmount="autocalcAmount"
          @accountChanged="evh_5534025912102772_accountChanged($event, {})"
          @autocalcAmountChanged="
            evh_5534025912102772_autocalcAmountChanged($event, {})
          "
        >
        </FieldsetPaymentGeneral>
        <ItpRow>
          <ItpCol :w="12" form>
            <ItpFormGroup
              :label-cols-md="2"
              :content-cols-md="10"
              name="attachments"
              :label="gettext('Attachments')"
              labelAlign="right"
            >
              <UcAttachmentsPanel
                layout="wrap"
                :parent="item"
                :pending="pendingAttachments"
                :readonly="item.isReadonly"
                :reload="attachmentsReload"
                @stage="evh_6802360265496969_stage($event, {})"
                @unstage="evh_6802360265496969_unstage($event, {})"
              >
              </UcAttachmentsPanel>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr />
      <UcPaymentInvoices
        :payment="item"
        :reload="lastLoaded"
        @recalculate="evh_3083451129821185_recalculate($event, {})"
      >
      </UcPaymentInvoices>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          :disabled="!forms.payment || !forms.payment.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="!isNew && !item.isReadonly"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Command', 'Complete')"
          :disabled="!isNew && item.readonly"
          variant="success"
          icon="fa-check"
          class="me-2"
          v-if="!isNew && !item.isReadonly"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Create payment')"
          :disabled="!forms.payment || !forms.payment.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="isNew"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Create another payment')"
          :disabled="!forms.payment || !forms.payment.submittable"
          variant="light"
          icon="mdi:forward"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="!isNew"
          @click.prevent.stop="
            evh_7253299514750264_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
